import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface LazySectionProps {
  children: ReactNode;
  /**
   * Min-height reserved for the placeholder before children mount, to
   * avoid layout shift. Accepts any CSS length. Default '50vh'.
   */
  minHeight?: number | string;
  /** IntersectionObserver rootMargin. Default '600px' (prefetch below-fold). */
  rootMargin?: string;
  className?: string;
}

/**
 * Renders a min-height placeholder until the section nears the viewport,
 * then mounts its children (once). Pairs with React.lazy + <Suspense> for
 * below-the-fold landing sections so off-screen work is deferred.
 */
export function LazySection({
  children,
  minHeight = '50vh',
  rootMargin = '600px',
  className,
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;

    // No IntersectionObserver (SSR / old env) → render immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  const style: CSSProperties | undefined = visible
    ? undefined
    : { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight };

  return (
    <div ref={ref} className={className} style={style}>
      {visible ? children : null}
    </div>
  );
}
